import React, { useState, useRef, useEffect, useMemo } from 'react';
import { X, Shield, Zap } from 'lucide-react';

const CharacterIntroModal = ({ isOpen, onClose, onComplete, hasWatchedBefore }) => {
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [characterRevealed, setCharacterRevealed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [showControls, setShowControls] = useState(true);

  // Memoized character videos to prevent recreation on every render
  const characterVideos = useMemo(() => [
    {
      id: 'raaghav_intro',
      title: 'Meet Raaghav',
      subtitle: 'The Cyber Intruder',
      description: 'Raaghav is a skilled hacker with a knack for breaching even the most secure systems. He thrives in the shadows of the digital world.',
      videoPath: '/assets/videos/raaghav_intro.mp4',
      character: 'Raaghav',
      color: '#3B82F6', // Blue
      accent: '#1E40AF',
      icon: Shield,
      specialEffect: 'cyber-pulse',
      tagline: 'Defending the Digital Frontier',
      abilities: ['Threat Analysis', 'Security Architecture', 'Incident Response'],
      storyPhase: 'intro'
    },
    {
      id: 'shanaya_intro', 
      title: 'Meet Shanaya',
      subtitle: 'The new recruit',
      description: 'Shanaya is a brilliant professional analyst known for her keen eye for details and efficiency in her work place. She joins Raaghav\'s former employer in the technology team.',
      videoPath: '/assets/videos/shanaya_intro.mp4',
      character: 'Shanaya',
      color: '#EC4899', // Pink
      accent: '#BE185D',
      icon: Zap,
      specialEffect: 'data-stream',
      tagline: 'Analyzing Every Byte',
      abilities: ['Forensic Analysis', 'Vulnerability Assessment', 'Risk Management'],
      storyPhase: 'intro'
    }
  ], []);

  const currentVideo = characterVideos[currentVideoIndex];

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !isInitialized) {
      setCurrentVideoIndex(0);
      setIsPlaying(false);
      setVideoProgress(0);
      setCharacterRevealed(false);
      setIsInitialized(true);
      
      // Start the intro sequence after a short delay
      setTimeout(() => {
        setCharacterRevealed(true);
        setIsPlaying(true);
      }, 1000);
    }
  }, [isOpen, isInitialized]);

  // Reset initialization state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen]);

  // Handle video playback
  useEffect(() => {
    if (isPlaying && videoRef.current) {
      const video = videoRef.current;
      
      // Simple and reliable video loading
      video.src = currentVideo.videoPath;
      video.load();
      
      // Play the video when it's ready
      const handleCanPlay = () => {
        video.play().catch(console.error);
      };
      
      video.addEventListener('canplay', handleCanPlay);
      
      return () => {
        video.removeEventListener('canplay', handleCanPlay);
      };
    } else if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [isPlaying, currentVideo.videoPath]);

  const handleVideoEnd = () => {
    const nextIndex = currentVideoIndex + 1;
    
    if (nextIndex < characterVideos.length) {
      // Move to next video
      setCurrentVideoIndex(nextIndex);
      setIsPlaying(true);
    } else {
      // All videos completed
      setIsPlaying(false);
      setTimeout(() => {
        onComplete();
      }, 1000);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const progress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
      setVideoProgress(progress);
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleClose = () => {
    setIsInitialized(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 w-full h-full bg-black z-[10000] flex items-center justify-center">
      <style>
        {`
          @keyframes fade-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slide-in-right {
            from { opacity: 0; transform: translateX(50px); }
            to { opacity: 1; transform: translateX(0); }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-10px); }
            60% { transform: translateY(-5px); }
          }
          
          @keyframes cinematic-zoom {
            from { transform: scale(1.1); }
            to { transform: scale(1); }
          }
          
          @keyframes data-stream-flow {
            0% { transform: translateX(-100%); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateX(100%); opacity: 0; }
          }
          
          @keyframes cyber-pulse-animation {
            0%, 100% { box-shadow: 0 0 5px ${currentVideo?.color || '#3B82F6'}, 0 0 10px ${currentVideo?.color || '#3B82F6'}, 0 0 15px ${currentVideo?.color || '#3B82F6'}; }
            50% { box-shadow: 0 0 10px ${currentVideo?.color || '#3B82F6'}, 0 0 20px ${currentVideo?.color || '#3B82F6'}, 0 0 30px ${currentVideo?.color || '#3B82F6'}; }
          }
          
          .fade-in { animation: fade-in 0.8s ease-out; }
          .slide-in-right { animation: slide-in-right 0.6s ease-out; }
          .pulse-animation { animation: pulse 2s infinite; }
          .bounce-animation { animation: bounce 1s infinite; }
          .cinematic-zoom { animation: cinematic-zoom 2s ease-out; }
          .cyber-pulse { animation: cyber-pulse-animation 2s infinite; }
          
          .data-stream::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2px;
            background: linear-gradient(90deg, transparent, ${currentVideo?.color || '#3B82F6'}, transparent);
            animation: data-stream-flow 3s infinite;
          }
        `}
      </style>

      {/* Background Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-30"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationName: 'pulse',
              animationDuration: '3s',
              animationIterationCount: 'infinite',
              animationDelay: `${Math.random() * 3}s`
            }}
          />
        ))}
      </div>


      {/* Video Player */}
      <div className="relative w-full h-full flex flex-col">
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 p-4 bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ backgroundColor: currentVideo.color }}
                >
                  {React.createElement(currentVideo.icon, { size: 24 })}
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gray-800 rounded-full flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{currentVideo.title}</h2>
                <p className="text-lg text-gray-300">{currentVideo.subtitle}</p>
                <p className="text-sm text-blue-400">{currentVideo.tagline}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-400">
                <span className="relative flex h-2 w-2 mr-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                LIVE
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-400">{currentVideo.character.toUpperCase()}</p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Video Container - Takes most space */}
        <div className="flex-1 relative bg-black overflow-hidden">
          <video
            ref={videoRef}
            key={currentVideoIndex}
            className="w-full h-full object-contain"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnd}
            muted={isMuted}
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>

          {/* Bottom spacer overlay for consistent visual gap */}
          <div 
            className="absolute left-0 right-0 bottom-0 h-4 bg-black/95 pointer-events-none"
          />

          {/* Special Effects Overlay */}
          {characterRevealed && (
            <div className={`absolute inset-0 pointer-events-none ${currentVideo.specialEffect}`}>
              {currentVideo.specialEffect === 'cyber-pulse' && (
                <div className="absolute top-4 left-4 w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              )}
              {currentVideo.specialEffect === 'data-stream' && (
                <div className="absolute top-4 right-4 w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
              )}
            </div>
          )}

          {/* Video Controls Overlay */}
          {/* {showControls && (
            <div 
              className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"
              onMouseMove={handleMouseMove}
            >
              <div className="absolute bottom-0 left-0 right-0 p-6">
                {/* Progress Bar */}
                {/*<div className="mb-4">
                  <div className="w-full bg-white/20 rounded-full h-1">
                    <div 
                      className="h-1 rounded-full transition-all duration-300"
                      style={{ 
                        width: `${videoProgress}%`,
                        backgroundColor: currentVideo.color 
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default CharacterIntroModal;